import urlcat from 'urlcat';
import type { AxiosRequestConfig } from 'axios';
import type {
    Campaign as CampaignModel,
    CampaignMediaGroupParameter,
    CampaignMediaGroupParameterPayload,
    CampaignMediaGroupName,
    CampaignCreateRequest,
    CampaignPcaExportRequest,
    CampaignCreativeAutoAssignRequest,
    CampaignCreativeForceAssignRequest,
    CampaignCreativeMassUnAssignRequest,
    CampaignCreativesAutoGenerateGroupsResponse,
    CampaignCreativesAutoGroupsGenerateRequest,
    CampaignCreativesResponse,
    CampaignCreativeUnAssignRequest,
    CampaignDisplayIssuesResponse,
    CampaignDisplaySchedule,
    CampaignError,
    CampaignSyncLineItemsRequest,
    Displays_LineItem,
    HttpProgressUpdate,
    ListSearchOptions,
    ListSearchResponse,
    MediaGroup,
    MediaTable,
    ModuleConstructorProps,
    Owner,
    paramGet,
    ParamPost,
    SaveCampaignPopSettingsPayload,
    Schedule,
    ScheduleGet,
    ScheduleRuleGet,
    SetCampaignNoticesRequest,
    AllowedVenueUploadResponse,
    CampaignAllowedVenue,
    CampaignInsertionOrder,
    InsertionOrder,
    CampaignInsertionOrderPlaceholder,
    InsertionOrderDetailPayload,
    InsertionOrderDetailResponse,
    InsertionOrderPlaceholderPayload,
    InsertionOrderKey,
    InsertionOrderRule,
    InsertionOrderRuleBatchResult,
    InsertionOrderRulePayload,
} from '../Types';
import { generateArrayQSParams } from '../Lib';
import { BaseCreateFunc, BaseEntityModuleFuncs, ListEndpointFunc } from './BaseEntityModuleFuncs';

interface DisplayData {
    id: number;
    name: string;
    partner_id: number;
    ext_id: string;
    unilive_id: number;
    market_id: number;
    frame_id: string;
    previous_frame_id: string | null;
    frame_type_id: number;
    type: string;
    description: string;
    brochurelink: string;
    env: string;
    slots: number;
    sov: number;
    slot_length: number;
    animation: string;
    size: string;
    sales_code: string;
    surface_area: string;
    height: string;
    width: string;
    brochure: string | null;
    location: string;
    address: string;
    postcode: string;
    lat: string;
    long: string;
    town: string;
    tv_region: string;
    conurbation: string;
    channel: string;
    frame: string;
    status: number;
    created_at: string | null;
    updated_at: string;
    updated_by: string;
    deleted_at: string | null;
    energy_conversion_override: string | null;
    energy_uses_per_second: string | null;
    salesforce_display_id: string;
    schedules: Schedule[];
    schedulesCount: number;
    mediaOwners: Owner[];
}
type LineItemStatusPayload = Record<string, { status: number; comments: string }>;

interface CampaignLineItemReview {
    id: number;
    campaign_id: number;
    campaign_name: string;

    buyer_id: number;
    buyer_name: string;

    display_media_owner_id: number;
    display_media_owner_name: string;

    display_line_item_name: string;
    display_line_item_id: string;

    impacts_percentage: number;
    plays_percentage: number;

    status: number;

    comments: string | null;

    updated_by_user: number | null;
    updated_by_user_at: string | null;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/** UL-8136 -- what a placeholder write also answers with: the campaign's new sum of targets. */
type WithExpectedImpacts = { expected_impacts?: number | null };

const Campaign = ({ endpoints, httpClient, baseUrl }: ModuleConstructorProps) => ({
    ...BaseEntityModuleFuncs<CampaignModel>({ endpoints, httpClient, baseUrl }, 'Campaign'),
    create: (data: CampaignCreateRequest, includes?: string[], config?: AxiosRequestConfig) =>
        BaseCreateFunc<CampaignModel>({ endpoints, httpClient, baseUrl }, 'Campaign')(
            data,
            includes,
            config,
        ),
    syncLineItems: (
        id: number,
        data: CampaignSyncLineItemsRequest,
        source?: string,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) => {
        const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
        const sourceParam = source
            ? `${includeParams ? '&' : '?'}source=${encodeURIComponent(source)}`
            : '';
        const url = `${urlcat(endpoints.Entities.Campaign.syncLineItems, {
            id,
        })}${includeParams}${sourceParam}`;
        return httpClient.post<{ data: CampaignModel }>(url, data, config);
    },
    // ---------------------------------------------------------------------------------------
    // UL-7912 -- DSP insertion order targeting.
    //
    // The catalogue is read-only and keyed on (customer, insertion_order_id); everything else is
    // campaign-scoped. Nothing writes the resolved set directly: ticking insertion orders saves a
    // single-condition exact rule each, so a manual pick and a standing rule are one mechanism and
    // detach the same way.
    // ---------------------------------------------------------------------------------------

    /** Search the standing catalogue. Filter with `filter.customer`, search `search.advertiser` etc. */
    insertionOrderCatalogue: (options: ListSearchOptions = {}, config?: AxiosRequestConfig) =>
        ListEndpointFunc<ListSearchResponse<InsertionOrder>>(httpClient)(
            { endpoint: endpoints.Entities.Campaign.insertionOrderCatalogue, options },
            config,
        ),

    /** The insertion orders currently attached to the campaign. */
    insertionOrders: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: CampaignInsertionOrder[] }>(
            urlcat(endpoints.Entities.Campaign.insertionOrders, { id }),
            config,
        ),

    /** The campaign's targeting rules, each with its conditions. */
    insertionOrderRules: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: InsertionOrderRule[] }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderRules, { id }),
            config,
        ),

    createInsertionOrderRule: (
        id: number,
        payload: InsertionOrderRulePayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data: InsertionOrderRule }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderRules, { id }),
            payload,
            config,
        ),

    updateInsertionOrderRule: (
        id: number,
        ruleId: number,
        payload: InsertionOrderRulePayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: InsertionOrderRule }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderRule, { id, ruleId }),
            payload,
            config,
        ),

    /** Detaches precisely the insertion orders this rule brought in; others keep their own rows. */
    deleteInsertionOrderRule: (id: number, ruleId: number, config?: AxiosRequestConfig) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.insertionOrderRule, { id, ruleId }),
            {},
            config,
        ),

    /**
     * Save several rules and resolve once. Used by both modals: a tick is a rule, and one Apply of
     * the parameters modal can make a rule per identifier line. Duplicates come back in `skipped`.
     */
    createInsertionOrderRules: (
        id: number,
        rules: InsertionOrderRulePayload[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ success: boolean; data: InsertionOrderRuleBatchResult }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderRulesBatch, { id }),
            { rules },
            config,
        ),

    /** Delete several rules; each detaches exactly what it brought in. */
    deleteInsertionOrderRules: (id: number, ruleIds: number[], config?: AxiosRequestConfig) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.insertionOrderRules, { id }),
            { rule_ids: ruleIds },
            config,
        ),

    /**
     * CPM and the impressions target, keyed on the catalogue's own key. Survives the rule that
     * attached the insertion order. Writes only the keys sent (UL-8131), and answers with the
     * campaign's new `expected_impacts`.
     */
    updateInsertionOrderDetail: (
        id: number,
        payload: InsertionOrderDetailPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<InsertionOrderDetailResponse>(
            urlcat(endpoints.Entities.Campaign.insertionOrderDetail, { id }),
            payload,
            config,
        ),

    /** Re-resolve every rule now, rather than waiting for the hourly pass. */
    syncInsertionOrders: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post(urlcat(endpoints.Entities.Campaign.insertionOrderSync, { id }), {}, config),

    /** Point the given insertion orders at a schedule, all or nothing. */
    applyInsertionOrderSchedule: (
        id: number,
        payload: { schedule_id: number; insertion_orders: InsertionOrderKey[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put(
            urlcat(endpoints.Entities.Campaign.insertionOrderSchedule, { id }),
            payload,
            config,
        ),

    /**
     * Return the given insertion orders to the campaign Default. Pass `schedule_id` to move only those
     * currently on that schedule -- which is what － on a specific schedule means.
     */
    removeInsertionOrderSchedule: (
        id: number,
        payload: { insertion_orders: InsertionOrderKey[]; schedule_id?: number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.insertionOrderSchedule, { id }),
            payload,
            config,
        ),

    // UL-8136 -- planned lines (placeholders). Every write answers with the campaign's new
    // `expected_impacts` in `data`, beside the placeholder (or the linked IO) where there is one.

    /** The campaign's planned lines. */
    insertionOrderPlaceholders: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: CampaignInsertionOrderPlaceholder[] }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderPlaceholders, { id }),
            config,
        ),

    /** A new planned line, on the campaign Default. */
    createInsertionOrderPlaceholder: (
        id: number,
        payload: { label: string; impressions_target?: number | null },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data: CampaignInsertionOrderPlaceholder & WithExpectedImpacts }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderPlaceholders, { id }),
            payload,
            config,
        ),

    /** Writes only the keys sent, as the insertion order detail endpoint does. */
    updateInsertionOrderPlaceholder: (
        id: number,
        placeholderId: number,
        payload: InsertionOrderPlaceholderPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: CampaignInsertionOrderPlaceholder & WithExpectedImpacts }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderPlaceholder, { id, placeholderId }),
            payload,
            config,
        ),

    /** Removes the planned line and its target from the campaign. */
    deleteInsertionOrderPlaceholder: (
        id: number,
        placeholderId: number,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{ data?: WithExpectedImpacts }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderPlaceholder, { id, placeholderId }),
            {},
            config,
        ),

    /**
     * Moves the planned line's target and schedule onto one catalogue insertion order, which the API
     * attaches itself, and deletes the placeholder. 422 when that IO already has a target.
     */
    linkInsertionOrderPlaceholder: (
        id: number,
        placeholderId: number,
        payload: InsertionOrderKey,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data?: WithExpectedImpacts }>(
            urlcat(endpoints.Entities.Campaign.insertionOrderPlaceholderLink, {
                id,
                placeholderId,
            }),
            payload,
            config,
        ),

    /** The campaign's allowed venue list. Empty means every screen is allowed, not none. */
    allowedVenues: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ count: number; uploaded_at: string | null; data: CampaignAllowedVenue[] }>(
            urlcat(endpoints.Entities.Campaign.allowedVenues, { id }),
            config,
        ),

    /**
     * Clear the list (soft delete, like a superseded upload). Afterwards every screen is allowed and
     * nothing can be unbooked, so confirm with the user first. `vistar_us2` campaigns only.
     */
    deleteAllowedVenues: (id: number, config?: AxiosRequestConfig) =>
        httpClient.delete<{ success: boolean; data: { deleted: number } }>(
            urlcat(endpoints.Entities.Campaign.allowedVenues, { id }),
            config,
        ),

    /**
     * Validate, then write. Send with `dryRun` to get the summary without saving anything, then send
     * the same file again without it when the user presses Save.
     *
     * Show the summary, not just the success: a file that read 687 rows and inserted 640 means 47
     * screens the agency listed will report as unbooked.
     */
    uploadAllowedVenues: (id: number, file: File, dryRun: boolean, config?: AxiosRequestConfig) => {
        const body = new FormData();
        body.append('file', file);
        body.append('dry_run', dryRun ? '1' : '0');

        return httpClient.post<AllowedVenueUploadResponse>(
            urlcat(endpoints.Entities.Campaign.allowedVenuesUpload, { id }),
            body,
            config,
        );
    },

    lineItems: (id: number, options: ListSearchOptions = {}, config?: AxiosRequestConfig) =>
        ListEndpointFunc<ListSearchResponse<Displays_LineItem>>(httpClient)(
            {
                endpoint: urlcat(endpoints.Entities.Campaign.lineItems, { id }),
                options,
            },
            config,
        ),

    downloadLink: (
        campaignId: number,
        {
            line_item_ids = [],
            creative_ids = [],
            include_display_specs,
            include_creatives,
            include_pop_shots,
        }: {
            line_item_ids?: string[];
            creative_ids?: number[];
            include_display_specs?: boolean;
            include_creatives?: boolean;
            include_pop_shots?: boolean;
        },
    ) =>
        `${baseUrl}${urlcat(endpoints.Entities.Campaign.download, {
            id: campaignId,
            include_display_specs: include_display_specs ? '1' : '0',
            include_creatives: include_creatives ? '1' : '0',
            include_pop_shots: include_pop_shots ? '1' : '0',
        })}${generateArrayQSParams(line_item_ids, 'line_item_ids', '&')}${generateArrayQSParams(
            creative_ids,
            'creative_ids',
            '&',
        )}`,
    shareUrl: (id: number, invite_type = 'agency', config?: AxiosRequestConfig) =>
        httpClient.post<{ url: string; url_legacy: string }>(
            urlcat(endpoints.Entities.Campaign.shareUrl, {
                id,
            }),
            { invite_type },
            config,
        ),
    unshareUrl: (
        id: number,
        data: { user_id: number | string; link_type: string },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ success: boolean }>(
            urlcat(endpoints.Entities.Campaign.unShareUrl, {
                id,
            }),
            data,
            config,
        ),
    overrideBudgetSummary: (
        id: number,
        price: number,
        reason: string,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) => {
        const includesString = generateArrayQSParams(includes, 'include', '?');
        return httpClient.put<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.overrideBudgetSummary, {
                id,
            })}${includesString}`,
            { price, reason },
            config,
        );
    },
    setNotices: (
        id: number,
        data: SetCampaignNoticesRequest,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.setNotices, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            data,
            config,
        ),
    resetNotices: (id: number, includes?: string[], config?: AxiosRequestConfig) =>
        httpClient.get<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.resetNotices, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            config,
        ),
    downloadPCA: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post<{ success: boolean }>(
            urlcat(endpoints.Entities.Campaign.downloadPCA, {
                id,
            }),
            config,
        ),
    archive: (id: number, archive: boolean, config?: AxiosRequestConfig) =>
        httpClient.post<{
            data: CampaignModel;
        }>(
            urlcat(endpoints.Entities.Campaign.archive, {
                id,
            }),
            { archive },
            config,
        ),
    creatives: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<CampaignCreativesResponse>(
            urlcat(endpoints.Entities.Campaign.creatives, {
                id,
            }),
            config,
        ),
    autoGenerateCreativeGroups: (id: number, resolutions?: string[], config?: AxiosRequestConfig) =>
        httpClient.post<CampaignCreativesAutoGenerateGroupsResponse>(
            urlcat(endpoints.Entities.Campaign.autoGenerateCreativeGroups, {
                id,
            }),
            { resolutions },
            config,
        ),
    createAutoGeneratedGroups: (
        id: number,
        data: CampaignCreativesAutoGroupsGenerateRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<CampaignCreativesResponse>(
            urlcat(endpoints.Entities.Campaign.createAutoGeneratedGroups, {
                id,
            }),
            data,
            config,
        ),
    auditEvent: (id: number, data: { audit_event: string }) =>
        httpClient.post(urlcat(endpoints.Entities.Campaign.auditEvent, { id }), data),
    forceAssignCreativesToLineItems: (
        id: number,
        data: CampaignCreativeForceAssignRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<CampaignCreativesResponse>(
            `${urlcat(endpoints.Entities.Campaign.forceAssignCreativesToLineItems, {
                id,
            })}${generateArrayQSParams(['campaignDisplays'], 'include', '?')}`,
            data,
            config,
        ),
    autoAssignCreativesToLineItems: (
        id: number,
        {
            creatives,
            lineItems,
            creativeGroups,
            filters = {},
            searchables = {},
        }: CampaignCreativeAutoAssignRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<CampaignCreativesResponse>(
            urlcat(endpoints.Entities.Campaign.autoAssignCreativesToLineItems, {
                id,
                ...searchables,
            }),
            { creatives, lineItems, creativeGroups, ...filters, ...searchables },
            config,
        ),
    forceUnAssignCreativesFromLineItems: (
        id: number,
        data: CampaignCreativeUnAssignRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<CampaignCreativesResponse>(
            urlcat(endpoints.Entities.Campaign.forceUnAssignCreativesFromLineItems, {
                id,
            }),
            data,
            config,
        ),
    massUnAssignCreativesFromLineItems: (
        id: number,
        {
            line_item_ids,
            creative_group_ids,
            creative_ids,
            filters = {},
            searchables = {},
        }: CampaignCreativeMassUnAssignRequest,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<CampaignCreativesResponse>(
            urlcat(endpoints.Entities.Campaign.massUnAssignCreativesFromLineItems, {
                id,
                ...searchables,
            }),
            { line_item_ids, creative_group_ids, creative_ids, ...filters, ...searchables },
            config,
        ),
    displayIssues: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<CampaignDisplayIssuesResponse>(
            urlcat(endpoints.Entities.Campaign.displayIssues, {
                id,
            }),
            config,
        ),
    errors: (id: number, includes?: string[], config?: AxiosRequestConfig) =>
        httpClient.get<{ data: CampaignError[] }>(
            `${urlcat(endpoints.Entities.Campaign.errors, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            config,
        ),
    validate: (id: number, includes?: string[], config?: AxiosRequestConfig) =>
        httpClient.post<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.validate, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            config,
        ),
    deleteBookingSummary: (
        campaignId: number,
        bookingSummaryId: number,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.deleteBookingSummary, {
                id: campaignId,
                bookingSummary: bookingSummaryId,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            null,
            config,
        ),
    deleteLegacyBookingSummary: (
        campaignId: number,
        bookingSummaryFileName: string,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.deleteLegacyBookingSummary, {
                id: campaignId,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            { name: bookingSummaryFileName },
            config,
        ),
    uploadBookingSummary: (
        id: number,
        {
            formData,
            onProgress,
        }: {
            /**
             * file: File - the actual file to upload
             */
            formData;
            onProgress?: HttpProgressUpdate;
        },
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.postFile<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.uploadBookingSummary, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            formData,
            onProgress,
            {},
            config,
        ),
    creativeValidation: (id: number, formData: FormData, config?: AxiosRequestConfig) =>
        httpClient.postFile<{
            status: string;
            alert: string;
            job_status_id: number;
            warning?: string;
        }>(
            urlcat(endpoints.Entities.Campaign.creativeValidation, { id }),
            formData,
            undefined,
            {},
            config,
        ),
    syncContacts: (
        id: number,
        contacts: number[],
        includes?: string[],
        config?: AxiosRequestConfig,
    ) => {
        const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
        return httpClient.post<{ data: CampaignModel }>(
            `${urlcat(endpoints.Entities.Campaign.syncContacts, {
                id,
            })}${includeParams}`,
            { additional_contacts: contacts },
            config,
        );
    },
    updatePopSettings: (
        id: number,
        data: SaveCampaignPopSettingsPayload,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) => {
        const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
        return httpClient.post<{ data: CampaignModel }>(
            `${urlcat(endpoints.Entities.Campaign.updatePopSettings, {
                id,
            })}${includeParams}`,
            data,
            config,
        );
    },
    scheduleLineItems: (
        id: number | string,
        options: ListSearchOptions = {},
        config?: AxiosRequestConfig,
    ) =>
        ListEndpointFunc<ListSearchResponse<Displays_LineItem>>(httpClient)(
            {
                endpoint: urlcat(endpoints.Entities.Campaign.scheduleLineItems, { id }),
                options,
            },
            config,
        ),
    screenFromSchedule: (
        id: number | string,
        lineItemId: string | number,
        options: ListSearchOptions = {},
        config?: AxiosRequestConfig,
    ) =>
        ListEndpointFunc<ListSearchResponse<CampaignDisplaySchedule>>(httpClient)(
            {
                endpoint: urlcat(endpoints.Entities.Campaign.screenFromSchedule, {
                    id,
                    lineItemId,
                }),
                options,
            },
            config,
        ),
    schedules: (
        id: number | string,
        includes?: string[],
        page?: number,
        perPage?: number,
        search?: string,
        config?: AxiosRequestConfig,
    ) => {
        const hasIncludes = (includes?.length ?? 0) > 0;
        const queryParams = `${generateArrayQSParams(includes, 'include', '?')}${
            hasIncludes ? (page ? `&page=${page}` : '') : page ? `?page=${page}` : ''
        }${
            hasIncludes || page
                ? perPage
                    ? `&perPage=${perPage}`
                    : ''
                : perPage
                ? `?perPage=${perPage}`
                : ''
        }${search ? `&search.name=${search}` : ''}&orderBy=created_at&direction=asc`;
        return httpClient.get<{ data: ScheduleGet }>(
            `${urlcat(endpoints.Entities.Campaign.schedules, {
                id,
            })}${queryParams}`,
            config,
        );
    },
    scheduleRules: (
        id: number | string,
        scheduleId: number | string,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.get<{ data: ScheduleRuleGet }>(
            `${urlcat(endpoints.Entities.Campaign.scheduleRules, {
                id,
                scheduleId,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            config,
        ),
    attachSchedules: (
        id: number | string,
        data: {
            lineItems?: string[] | number[];
            displays?: string[] | number[];
            schedules?: string[] | number[];
        },
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            `${urlcat(endpoints.Entities.Campaign.attachSchedules, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            data,
            config,
        ),
    detachSchedules: (
        id: number | string,
        data: {
            lineItems?: string[] | number[];
            displays?: string[] | number[];
            schedules?: string[] | number[];
        },
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            `${urlcat(endpoints.Entities.Campaign.detachSchedules, {
                id,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            data,
            config,
        ),
    deleteSchedules: (
        id: number | string,
        scheduleId: number | string,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.deleteSchedules, { id, scheduleId }),
            config,
        ),
    campaignPowerpoint: (
        id: number | string,
        third_color: boolean,
        powerpoint_template = 'linear_report',
        skin_id: number,
        language: string,
        start_date?: string,
        end_date?: string,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            urlcat(endpoints.Entities.Campaign.campaignPowerpoint, { id }),
            { third_color, powerpoint_template, skin_id, language, start_date, end_date },
            config,
        ),
    campaignPdfSend: (
        id: number | string,
        data: {
            powerpoint_template: 'Linear_report' | 'DSP_report' | 'custom';
            skin_id: number;
            language: 'GB' | 'US';
            start_date?: string;
            end_date?: string;
            third_color?: 'true' | 'false';
            type: 'pca' | 'inflight';
            emails: string[];
        },
        config?: AxiosRequestConfig,
    ) => httpClient.post(urlcat(endpoints.Entities.Campaign.campaignPdfSend, { id }), data, config),
    campaignPowerpointSend: (
        id: number | string,
        data: {
            powerpoint_template: 'Linear_report' | 'DSP_report' | 'custom';
            skin_id: number;
            language: 'GB' | 'US';
            start_date?: string;
            end_date?: string;
            type: 'pca' | 'inflight';
            emails: string[];
        },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            urlcat(endpoints.Entities.Campaign.campaignPowerpointSend, { id }),
            data,
            config,
        ),
    listTargets: (
        id: number | string,
        includes?: string[],
        page = 1,
        perPage = 25,
        search?: string,
        orderBy = 'created_at',
        direction: 'asc' | 'desc' = 'asc',
        config?: AxiosRequestConfig,
    ) => {
        const includeParams = generateArrayQSParams(includes, 'include', '&');

        const queryParams = `?orderBy=${orderBy}&direction=${direction}${
            search ? `&search.name=${search}` : ''
        }${includeParams}${page ? `&page=${page}` : ''}${perPage ? `&perPage=${perPage}` : ''}`;

        const url = `${urlcat(endpoints.Entities.Campaign.listTargets, { id })}${queryParams}`;

        return httpClient.post<{
            data: {
                id: string | number;
                name: string;
                type: 'impacts' | 'plays';
                target: string | number;
                schedules: Schedule[];
            }[];
        }>(url, {}, config);
    },

    updateTargets: (
        campaignId: number | string,
        targetId: number | string,
        data: {
            name: string;
            type: 'impacts' | 'plays';
            target: string | number;
            schedules?: Schedule[];
        },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{
            data: {
                id: string | number;
                name: string;
                type: 'impacts' | 'plays';
                target: string | number;
                schedules?: Schedule[];
            };
        }>(
            urlcat(endpoints.Entities.Campaign.updateTargets, { id: campaignId, targetId }),
            data,
            config,
        ),
    createTargets: (
        campaignId: number | string,
        data: { name: string; type: 'impacts' | 'plays'; target: string | number },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{
            data: {
                id: string | number;
                name: string;
                type: 'impacts' | 'plays';
                target: string | number;
            };
        }>(urlcat(endpoints.Entities.Campaign.createTargets, { id: campaignId }), data, config),
    deleteTargets: (
        campaignId: number | string,
        targetId: number | string,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.deleteTargets, { id: campaignId, targetId }),
            config,
        ),
    attachTargetToSchedules: (
        campaignId: number | string,
        targetId: number | string,
        data: { schedules: number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ success: boolean }>(
            urlcat(endpoints.Entities.Campaign.attachTargetToSchedules, {
                id: campaignId,
                targetId,
            }),
            data,
            config,
        ),
    detachTargetFromSchedules: (
        campaignId: number | string,
        targetId: number | string,
        data?: { schedules: number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ success: boolean }>(
            urlcat(endpoints.Entities.Campaign.detachTargetFromSchedules, {
                id: campaignId,
                targetId,
            }),
            data,
            config,
        ),
    channelFrameGet: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ last_modified: number | null; url: string | null }>(
            urlcat(endpoints.Entities.Campaign.channelFrame, {
                id,
            }),
            config,
        ),
    channelFramePost: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post<{ alert: string; status: string }>(
            urlcat(endpoints.Entities.Campaign.channelFrame, {
                id,
            }),
            config,
        ),
    pcaExport: (
        id: number,
        formData: FormData | CampaignPcaExportRequest,
        config?: AxiosRequestConfig,
    ) => {
        const url = urlcat(endpoints.Entities.Campaign.pcaExport, { id });
        return httpClient.post<{ success: boolean }>(url, formData, config);
    },
    pcaImport: (
        id: number,
        { formData, onProgress }: { formData: FormData; onProgress: HttpProgressUpdate },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.postFile<{
            data: CampaignModel;
        }>(
            `${urlcat(endpoints.Entities.Campaign.pcaImport, {
                id,
            })}`,
            formData,
            onProgress,
            {},
            config,
        ),
    mediaList: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post<{ media: MediaTable[]; mediaGroups: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.popSettingsMediaTable, {
                id,
            }),
            config,
        ),
    GetMediaParameter: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: paramGet[] }>(
            urlcat(endpoints.Entities.Campaign.mediaTableParameter, {
                id,
            }),
            config,
        ),
    AddMediaParameter: (id: number, data: ParamPost, config?: AxiosRequestConfig) =>
        httpClient.post<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.mediaTableParameter, {
                id,
            }),
            data,
            config,
        ),
    getMediaGroups: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: CampaignMediaGroupName[] }>(
            urlcat(endpoints.Entities.Campaign.campaignMediaGroups, {
                id,
            }),
            config,
        ),
    deleteMediaGroups: (
        id: number,
        data: { group_name_ids: number[] | string[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{ data: CampaignMediaGroupName[] }>(
            urlcat(endpoints.Entities.Campaign.campaignMediaGroups, {
                id,
            }),
            data,
            config,
        ),
    getMediaGroupParameters: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: CampaignMediaGroupParameter[] }>(
            urlcat(endpoints.Entities.Campaign.mediaGroupParameter, {
                id,
            }),
            config,
        ),
    addMediaGroupParameter: (
        id: number,
        data: CampaignMediaGroupParameterPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: CampaignMediaGroupParameter[] }>(
            urlcat(endpoints.Entities.Campaign.mediaGroupParameter, {
                id,
            }),
            data,
            config,
        ),
    deleteMediaGroupParameters: (
        id: number,
        data: { group_parameter_ids: number[] | string[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{ data: CampaignMediaGroupParameter[] }>(
            urlcat(endpoints.Entities.Campaign.mediaGroupParameter, {
                id,
            }),
            data,
            config,
        ),
    UpdateMediaParameter: (id: number, data: ParamPost, config?: AxiosRequestConfig) =>
        httpClient.put<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.mediaTableParameter, {
                id,
            }),
            data,
            config,
        ),
    DeleteMultipleMediaParameters: (
        id: number,
        parameter_ids: number[] | string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.mediaTableParameter, {
                id,
            }),
            { parameter_ids },
            config,
        ),
    removeIdsFromMediaGroup: (
        id: number,
        data: { media_ids: number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.removeIdsFromMediaGroup, {
                id,
            }),
            data,
            config,
        ),
    campaignClassicMediaList: (id: number, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: { id: number; media_name: string }[] }>(
            urlcat(endpoints.Entities.Campaign.CampaignMediaList, { id }),
            config,
        ),
    campaignClassicMediaUpdate: (
        id: number,
        data: { media_name: string[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ data: { id: number; media_name: string }[] }>(
            urlcat(endpoints.Entities.Campaign.CampaignMediaList, { id }),
            data,
            config,
        ),
    deleteCampaignClassicMedia: (
        id: number,
        data: { campaign_media_ids: string[] | number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.delete(
            urlcat(endpoints.Entities.Campaign.deleteCampaignMedia, { id }),
            data,
            config,
        ),
    editGroup: (
        id: number,
        data: { group_name: string; media_ids?: number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.editGroup, {
                id,
            }),
            data,
            config,
        ),
    syncMedia: (id: number, config?: AxiosRequestConfig) =>
        httpClient.post<{ media: MediaTable[]; mediaGroup: MediaGroup }>(
            urlcat(endpoints.Entities.Campaign.syncMedia, {
                id,
            }),
            config,
        ),
    addDisplay: (
        id: number | string,
        data: { identifier: string[]; identifier_type: 'frame_id' | 'display_id' },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            urlcat(endpoints.Entities.Campaign.addDisplay, {
                id,
            }),
            data,
            config,
        ),
    removeDisplay: (
        id: number | string,
        data: { display_ids: number[] },
        config?: AxiosRequestConfig,
    ) => httpClient.post(urlcat(endpoints.Entities.Campaign.removeDisplay, { id }), data, config),
    removeUnknownDisplays: (
        id: number | string,
        data: { ids: number[] },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            urlcat(endpoints.Entities.Campaign.removeUnknownDisplays, { id }),
            data,
            config,
        ),
    listDisplays: (
        id: number | string,
        options: ListSearchOptions = {},
        config?: AxiosRequestConfig,
    ) =>
        ListEndpointFunc<ListSearchResponse<DisplayData>>(httpClient)(
            {
                endpoint: urlcat(endpoints.Entities.Campaign.listDisplays, { id }),
                options,
            },
            config,
        ),
    listPopDisplays: (
        id: number | string,
        options?: ListSearchOptions,
        config?: AxiosRequestConfig,
    ) =>
        ListEndpointFunc<ListSearchResponse<DisplayData>>(httpClient)(
            { endpoint: urlcat(endpoints.Entities.Campaign.listPopDisplays, { id }), options },
            config,
        ),

    displaysDownload: (
        id: number | string,
        data: {
            'search.any'?: string;
            filter?: any;
            'search.vendor_id': string;
        },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ message: string }>(
            urlcat(endpoints.Entities.Campaign.displaysDownload, { id }),
            data,
            config,
        ),
    listUnknownDisplays: (id: number | string, perPage = 25, config?: AxiosRequestConfig) =>
        httpClient.get<{ data: { id: number; frame_id: string; display_id: string | null }[] }>(
            urlcat(endpoints.Entities.Campaign.listUnknownDisplays, { id, perPage }),
            config,
        ),
    reImportUnknownDisplays: (id: number | string, config?: AxiosRequestConfig) =>
        httpClient.post(
            urlcat(endpoints.Entities.Campaign.reImportUnknownDisplays, { id }),
            {},
            config,
        ),
    downloadUnknownDisplays: (id: number | string, config?: AxiosRequestConfig) =>
        httpClient.post<string>(
            urlcat(endpoints.Entities.Campaign.downloadUnknownDisplays, { id }),
            {},
            config,
        ),
    getLineItemStatus: (id: number | string) =>
        httpClient.get<CampaignLineItemReview[]>(
            urlcat(endpoints.Entities.Campaign.lineItemStatus, { id }),
        ),
    editLineItemStatus: (
        id: number | string,
        data: LineItemStatusPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.put<CampaignLineItemReview[]>(
            urlcat(endpoints.Entities.Campaign.lineItemStatus, { id }),
            data,
            config,
        ),
});

export default Campaign;
